/**
 * Transfers view (task 21.2): beneficiary transfers (R3.5) and inter-account
 * transfers (R4.4).
 *
 * Two forms share the loaded account/beneficiary lists:
 *
 * - **To a beneficiary** — pick a source account and a saved beneficiary, enter
 *   an amount, submit `POST /transfers/beneficiary`.
 * - **Between my accounts** — pick distinct source + destination accounts, enter
 *   an amount, submit `POST /transfers/internal`.
 *
 * Each form is a two-stage flow: **form → review → commit**. "Continue" runs
 * the client-side checks and switches to a review card summarising the amount,
 * source (type · masked ref · available balance) and destination; only
 * "Confirm transfer" issues the API call, with exactly the same payload as
 * before. "Back / Edit" returns to the form with the values intact.
 *
 * Amounts are guided client-side (defence in depth — the server stays
 * authoritative): the field is masked so only a valid partial money string can
 * be typed (digits and a single decimal point with at most two fraction
 * digits), it carries `inputMode="decimal"` and helper text linked via
 * `aria-describedby`, `validateAmount` runs live so the Continue button is
 * disabled for an obviously-bad amount, the selected source's available
 * balance is shown next to the field and an amount above it disables Continue
 * with an inline warning, and the server envelope (`INVALID_AMOUNT`,
 * `SAME_ACCOUNT`, `INSUFFICIENT_FUNDS`, `NOT_AUTHORIZED`) still renders inline.
 * The internal form filters the destination list to exclude the selected
 * source, keeping the `SAME_ACCOUNT` local guard as a fallback. Each request
 * carries an idempotency key (in the API client) so a retry is safe. A success
 * shows "R500.00 sent to <name>" / "moved to <account>" and clears the amount.
 *
 * Both forms also take an optional **Reference** (statement text such as
 * "Rent March"): `maxLength` 30, validated live against the same charset the
 * Transfer_Service enforces ({@link validateReference}) with an inline error,
 * trimmed before send and omitted from the body when blank
 * ({@link normalizeReference}). The review card shows the reference when one
 * was entered, and a server `INVALID_REFERENCE` envelope is rendered as a
 * friendly inline message under the field instead of the raw notice.
 */

import { useId, useState, type FormEvent } from 'react';

import {
  accountsApi,
  beneficiariesApi,
  transfersApi,
  type Account,
  type Beneficiary,
} from '../api/index.js';
import { isApiError } from '../auth/index.js';
import { ErrorNotice } from '../ui/ErrorNotice.js';
import {
  REFERENCE_HINT,
  REFERENCE_INVALID_MESSAGE,
  REFERENCE_MAX_LENGTH,
  formatMoney,
  normalizeReference,
  validateAmount,
  validateReference,
} from '../ui/format.js';
import { useAction, useAsyncData } from '../ui/useAsync.js';

/** The two stages of each transfer form. */
type Stage = 'form' | 'confirm';

/**
 * Friendly message for a server-rejected reference (`INVALID_REFERENCE`), or
 * `undefined` for any other (or no) error so the generic notice renders it.
 */
function referenceServerError(error: unknown): string | undefined {
  return isApiError(error) && error.code === 'INVALID_REFERENCE'
    ? REFERENCE_INVALID_MESSAGE
    : undefined;
}

/** Review rows shared by both forms: From/To plus the reference when present. */
function reviewRows(
  from: string,
  to: string,
  reference: string | undefined,
): readonly { readonly label: string; readonly value: string }[] {
  const rows = [
    { label: 'From', value: from },
    { label: 'To', value: to },
  ];
  return reference !== undefined ? [...rows, { label: 'Reference', value: reference }] : rows;
}

/**
 * Mask a raw amount input to a valid partial money string: strip everything
 * except digits and dots, collapse to a single decimal point, and cap the
 * fraction at two digits. Runs in onChange so an invalid character never even
 * appears in the field, while still allowing in-progress values like "" or
 * "12." during typing (final validity is decided by {@link validateAmount}).
 */
function maskAmount(raw: string): string {
  // Keep only digits and dots.
  const cleaned = raw.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) {
    return cleaned;
  }
  // Keep the first dot; drop any subsequent dots from the fraction part.
  const integerPart = cleaned.slice(0, firstDot);
  const fractionPart = cleaned.slice(firstDot + 1).replace(/\./g, '').slice(0, 2);
  return `${integerPart}.${fractionPart}`;
}

/** Last four characters of an account id, for a masked reference display. */
function maskedRef(accountId: string): string {
  return `•••• ${accountId.slice(-4)}`;
}

/**
 * Display-only insufficient-funds check: true when a syntactically valid amount
 * exceeds the source's balance (both parsed from their decimal strings). The
 * server remains authoritative and its `INSUFFICIENT_FUNDS` still renders.
 */
function exceedsBalance(amount: string, source: Account | undefined): boolean {
  if (source === undefined || validateAmount(amount) !== undefined) {
    return false;
  }
  const requested = Number(amount.trim());
  const available = Number(source.balance);
  return Number.isFinite(requested) && Number.isFinite(available) && requested > available;
}

/** Amount field with the live "Available: …" hint and the exceeds-balance warning. */
function AmountField({
  id,
  amount,
  amountError,
  source,
  overBalance,
  onChange,
}: {
  readonly id: string;
  readonly amount: string;
  readonly amountError: string | undefined;
  readonly source: Account | undefined;
  readonly overBalance: boolean;
  readonly onChange: (next: string) => void;
}): JSX.Element {
  const hintId = useId();
  const availableId = useId();
  const warnId = useId();
  const describedBy = [hintId, source !== undefined ? availableId : '', overBalance ? warnId : '']
    .filter((s) => s.length > 0)
    .join(' ');

  return (
    <div className="esb-field">
      <label htmlFor={id} className="esb-field__label">Amount</label>
      <input
        id={id}
        className="esb-field__input"
        inputMode="decimal"
        autoComplete="off"
        value={amount}
        onChange={(e) => onChange(maskAmount(e.target.value))}
        aria-describedby={describedBy}
        aria-invalid={amountError !== undefined || overBalance}
        required
      />
      <span id={hintId} className="esb-field__hint">
        Amount in ZAR, e.g. 250.00.
      </span>
      {source !== undefined ? (
        <span id={availableId} className="esb-field__hint esb-field__hint--available">
          Available: {formatMoney(source.balance, source.currency)}
        </span>
      ) : null}
      {overBalance ? (
        <span id={warnId} className="esb-field__hint esb-field__hint--warn">
          Amount exceeds available balance.
        </span>
      ) : null}
      {amountError !== undefined ? <span role="alert" className="esb-inline-error">{amountError}</span> : null}
    </div>
  );
}

/**
 * Optional payment-reference field with helper text and an inline error. The
 * error shown is the live client-side check ({@link validateReference}) or, once
 * the service has rejected the reference, the friendly `INVALID_REFERENCE`
 * message — whichever the parent passes in.
 */
function ReferenceField({
  id,
  reference,
  referenceError,
  onChange,
}: {
  readonly id: string;
  readonly reference: string;
  readonly referenceError: string | undefined;
  readonly onChange: (next: string) => void;
}): JSX.Element {
  const hintId = useId();
  return (
    <div className="esb-field">
      <label htmlFor={id} className="esb-field__label">Reference</label>
      <input
        id={id}
        className="esb-field__input"
        type="text"
        inputMode="text"
        autoComplete="off"
        maxLength={REFERENCE_MAX_LENGTH}
        value={reference}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Payment reference (optional)"
        aria-describedby={hintId}
        aria-invalid={referenceError !== undefined}
      />
      <span id={hintId} className="esb-field__hint">{REFERENCE_HINT}</span>
      {referenceError !== undefined ? (
        <span role="alert" className="esb-inline-error">{referenceError}</span>
      ) : null}
    </div>
  );
}

/** Review card shown in the confirm stage: large amount + From/To rows + actions. */
function ReviewCard({
  amountText,
  rows,
  pending,
  onConfirm,
  onBack,
}: {
  readonly amountText: string;
  readonly rows: readonly { readonly label: string; readonly value: string }[];
  readonly pending: boolean;
  readonly onConfirm: () => void;
  readonly onBack: () => void;
}): JSX.Element {
  return (
    <div className="esb-review" role="group" aria-label="Review transfer">
      <p className="esb-review__lead">You're about to send</p>
      <p className="esb-review__amount">{amountText}</p>
      <dl className="esb-review__rows">
        {rows.map((row) => (
          <div key={row.label} className="esb-review__row">
            <dt className="esb-review__label">{row.label}</dt>
            <dd className="esb-review__value">{row.value}</dd>
          </div>
        ))}
      </dl>
      <div className="esb-review__actions">
        <button
          type="button"
          className="esb-btn esb-btn--primary"
          onClick={onConfirm}
          disabled={pending}
          autoFocus
        >
          {pending ? 'Transferring…' : 'Confirm transfer'}
        </button>
        <button type="button" className="esb-btn esb-btn--ghost" onClick={onBack} disabled={pending}>
          Back / Edit
        </button>
      </div>
    </div>
  );
}

/** Transfer to a saved beneficiary (R3.5). */
function BeneficiaryTransferForm({
  accounts,
  beneficiaries,
  onDone,
}: {
  readonly accounts: readonly Account[];
  readonly beneficiaries: readonly Beneficiary[];
  readonly onDone: () => void;
}): JSX.Element {
  const [stage, setStage] = useState<Stage>('form');
  const [sourceAccountId, setSource] = useState('');
  const [beneficiaryId, setBeneficiary] = useState('');
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState<string | undefined>(undefined);
  const [reference, setReference] = useState('');
  const [confirmation, setConfirmation] = useState<string | undefined>(undefined);
  const sourceId = useId();
  const beneId = useId();
  const amountId = useId();
  const referenceId = useId();

  const transfer = useAction(transfersApi.transferToBeneficiary);

  const resolvedSourceId = sourceAccountId || (accounts[0]?.accountId ?? '');
  const resolvedBeneficiaryId = beneficiaryId || (beneficiaries[0]?.beneficiaryId ?? '');
  const source = accounts.find((a) => a.accountId === resolvedSourceId);
  const beneficiary = beneficiaries.find((b) => b.beneficiaryId === resolvedBeneficiaryId);
  const overBalance = exceedsBalance(amount, source);
  const referenceError = validateReference(reference) ?? referenceServerError(transfer.error);
  const normalizedReference = normalizeReference(reference);

  /** Stage 1: validate and move to the review card (no API call yet). */
  function onContinue(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setConfirmation(undefined);
    const invalid = validateAmount(amount);
    setAmountError(invalid);
    if (invalid !== undefined || overBalance || validateReference(reference) !== undefined) {
      return;
    }
    setStage('confirm');
  }

  /** Stage 2: commit with the same payload as before (plus the optional reference). */
  async function onConfirm(): Promise<void> {
    const result = await transfer.run({
      sourceAccountId: sourceAccountId || (accounts[0]?.accountId ?? ''),
      beneficiaryId: beneficiaryId || (beneficiaries[0]?.beneficiaryId ?? ''),
      amount: amount.trim(),
      // Omit the key entirely when blank so the service stores null.
      ...(normalizedReference !== undefined ? { reference: normalizedReference } : {}),
    });
    if (result !== undefined) {
      const money = formatMoney(amount.trim(), source?.currency ?? 'ZAR');
      const who = beneficiary?.displayName ?? 'beneficiary';
      setConfirmation(`${money} sent to ${who}.`);
      setAmount('');
      setReference('');
    }
    // Return to the form on both success and failure so the error (if any) is
    // visible alongside the editable fields.
    setStage('form');
    if (result !== undefined) {
      onDone();
    }
  }

  const amountValid = validateAmount(amount) === undefined;
  const disabled =
    accounts.length === 0 ||
    beneficiaries.length === 0 ||
    transfer.pending ||
    !amountValid ||
    overBalance ||
    validateReference(reference) !== undefined;

  return (
    <form onSubmit={onContinue} aria-labelledby="bene-transfer-heading" noValidate className="esb-card">
      <h3 id="bene-transfer-heading" className="esb-subhead">Transfer to a beneficiary</h3>

      {beneficiaries.length === 0 ? (
        <p className="esb-empty">Add a beneficiary before making a transfer.</p>
      ) : null}

      {/* INVALID_REFERENCE is shown under the reference field instead. */}
      <ErrorNotice error={referenceServerError(transfer.error) === undefined ? transfer.error : undefined} />
      {confirmation !== undefined ? (
        <p role="status" className="esb-status">{confirmation}</p>
      ) : null}

      {stage === 'confirm' && source !== undefined && beneficiary !== undefined ? (
        <ReviewCard
          amountText={formatMoney(amount.trim(), source.currency)}
          rows={reviewRows(
            `${source.accountType} · ${maskedRef(source.accountId)} · Available ${formatMoney(source.balance, source.currency)}`,
            `${beneficiary.displayName} · ${beneficiary.accountNumber} · ${beneficiary.bankCode}`,
            normalizedReference,
          )}
          pending={transfer.pending}
          onConfirm={() => void onConfirm()}
          onBack={() => setStage('form')}
        />
      ) : (
        <>
          <div className="esb-field">
            <label htmlFor={sourceId} className="esb-field__label">From account</label>
            <select
              id={sourceId}
              className="esb-select"
              value={sourceAccountId}
              onChange={(e) => setSource(e.target.value)}
            >
              {accounts.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.accountType} — {formatMoney(a.balance, a.currency)}
                </option>
              ))}
            </select>
          </div>

          <div className="esb-field">
            <label htmlFor={beneId} className="esb-field__label">Beneficiary</label>
            <select
              id={beneId}
              className="esb-select"
              value={beneficiaryId}
              onChange={(e) => setBeneficiary(e.target.value)}
            >
              {beneficiaries.map((b) => (
                <option key={b.beneficiaryId} value={b.beneficiaryId}>
                  {b.displayName} — {b.accountNumber}
                </option>
              ))}
            </select>
          </div>

          <AmountField
            id={amountId}
            amount={amount}
            amountError={amountError}
            source={source}
            overBalance={overBalance}
            onChange={(next) => {
              setAmount(next);
              setAmountError(undefined);
            }}
          />

          <ReferenceField
            id={referenceId}
            reference={reference}
            referenceError={referenceError}
            onChange={(next) => {
              setReference(next);
              // Editing clears a stale server INVALID_REFERENCE notice.
              if (referenceServerError(transfer.error) !== undefined) {
                transfer.reset();
              }
            }}
          />

          <button type="submit" className="esb-btn esb-btn--primary" disabled={disabled}>
            Continue
          </button>
        </>
      )}
    </form>
  );
}

/** Inter-account transfer between two of the customer's own accounts (R4.4). */
function InternalTransferForm({
  accounts,
  onDone,
}: {
  readonly accounts: readonly Account[];
  readonly onDone: () => void;
}): JSX.Element {
  const [stage, setStage] = useState<Stage>('form');
  const [sourceAccountId, setSource] = useState('');
  const [destinationAccountId, setDestination] = useState('');
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState<string | undefined>(undefined);
  const [reference, setReference] = useState('');
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const [confirmation, setConfirmation] = useState<string | undefined>(undefined);
  const sourceId = useId();
  const destId = useId();
  const amountId = useId();
  const referenceId = useId();

  const transfer = useAction(transfersApi.transferInternal);

  const resolvedSource = sourceAccountId || (accounts[0]?.accountId ?? '');
  // Destination choices exclude the selected source so same-account cannot be
  // picked; if the stored destination is the excluded one, fall back to the
  // first remaining account.
  const destinationOptions = accounts.filter((a) => a.accountId !== resolvedSource);
  const resolvedDest =
    destinationOptions.find((a) => a.accountId === destinationAccountId)?.accountId ??
    (destinationOptions[0]?.accountId ?? '');
  const source = accounts.find((a) => a.accountId === resolvedSource);
  const destination = accounts.find((a) => a.accountId === resolvedDest);
  const overBalance = exceedsBalance(amount, source);
  const referenceError = validateReference(reference) ?? referenceServerError(transfer.error);
  const normalizedReference = normalizeReference(reference);

  /** Stage 1: validate and move to the review card (no API call yet). */
  function onContinue(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setConfirmation(undefined);
    setLocalError(undefined);
    const invalid = validateAmount(amount);
    setAmountError(invalid);
    if (invalid !== undefined || overBalance || validateReference(reference) !== undefined) {
      return;
    }
    // Client-side guard mirroring R4.8 (fallback — the select already filters);
    // server remains authoritative.
    if (resolvedSource === resolvedDest) {
      setLocalError('Source and destination accounts must be different.');
      return;
    }
    setStage('confirm');
  }

  /** Stage 2: commit with the same payload as before (plus the optional reference). */
  async function onConfirm(): Promise<void> {
    const result = await transfer.run({
      sourceAccountId: resolvedSource,
      destinationAccountId: resolvedDest,
      amount: amount.trim(),
      // Omit the key entirely when blank so the service stores null.
      ...(normalizedReference !== undefined ? { reference: normalizedReference } : {}),
    });
    if (result !== undefined) {
      const money = formatMoney(amount.trim(), source?.currency ?? 'ZAR');
      setConfirmation(`${money} moved to ${destination?.accountType ?? 'your account'}.`);
      setAmount('');
      setReference('');
    }
    setStage('form');
    if (result !== undefined) {
      onDone();
    }
  }

  const amountValid = validateAmount(amount) === undefined;
  const disabled =
    accounts.length < 2 ||
    transfer.pending ||
    !amountValid ||
    overBalance ||
    validateReference(reference) !== undefined;

  return (
    <form onSubmit={onContinue} aria-labelledby="internal-transfer-heading" noValidate className="esb-card">
      <h3 id="internal-transfer-heading" className="esb-subhead">Transfer between my accounts</h3>

      {accounts.length < 2 ? (
        <p className="esb-empty">You need at least two accounts to make an internal transfer.</p>
      ) : null}

      {/* INVALID_REFERENCE is shown under the reference field instead. */}
      <ErrorNotice error={referenceServerError(transfer.error) === undefined ? transfer.error : undefined} />
      {localError !== undefined ? <p role="alert" className="esb-inline-error">{localError}</p> : null}
      {confirmation !== undefined ? <p role="status" className="esb-status">{confirmation}</p> : null}

      {stage === 'confirm' && source !== undefined && destination !== undefined ? (
        <ReviewCard
          amountText={formatMoney(amount.trim(), source.currency)}
          rows={reviewRows(
            `${source.accountType} · ${maskedRef(source.accountId)} · Available ${formatMoney(source.balance, source.currency)}`,
            `${destination.accountType} · ${maskedRef(destination.accountId)} · Balance ${formatMoney(destination.balance, destination.currency)}`,
            normalizedReference,
          )}
          pending={transfer.pending}
          onConfirm={() => void onConfirm()}
          onBack={() => setStage('form')}
        />
      ) : (
        <>
          <div className="esb-field">
            <label htmlFor={sourceId} className="esb-field__label">From account</label>
            <select
              id={sourceId}
              className="esb-select"
              value={resolvedSource}
              onChange={(e) => {
                setSource(e.target.value);
                if (e.target.value === destinationAccountId) {
                  setDestination('');
                }
              }}
            >
              {accounts.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.accountType} — {formatMoney(a.balance, a.currency)}
                </option>
              ))}
            </select>
          </div>

          <div className="esb-field">
            <label htmlFor={destId} className="esb-field__label">To account</label>
            <select
              id={destId}
              className="esb-select"
              value={resolvedDest}
              onChange={(e) => setDestination(e.target.value)}
            >
              {destinationOptions.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.accountType} — {formatMoney(a.balance, a.currency)}
                </option>
              ))}
            </select>
          </div>

          <AmountField
            id={amountId}
            amount={amount}
            amountError={amountError}
            source={source}
            overBalance={overBalance}
            onChange={(next) => {
              setAmount(next);
              setAmountError(undefined);
            }}
          />

          <ReferenceField
            id={referenceId}
            reference={reference}
            referenceError={referenceError}
            onChange={(next) => {
              setReference(next);
              // Editing clears a stale server INVALID_REFERENCE notice.
              if (referenceServerError(transfer.error) !== undefined) {
                transfer.reset();
              }
            }}
          />

          <button type="submit" className="esb-btn esb-btn--primary" disabled={disabled}>
            Continue
          </button>
        </>
      )}
    </form>
  );
}

/** Transfers panel: loads accounts + beneficiaries, renders both forms. */
export function TransfersView(): JSX.Element {
  const accounts = useAsyncData(() => accountsApi.listAccounts(), []);
  const beneficiaries = useAsyncData(() => beneficiariesApi.listBeneficiaries(), []);

  const accountList = accounts.data?.accounts ?? [];
  const beneficiaryList = beneficiaries.data?.beneficiaries ?? [];

  const reloadAll = (): void => {
    accounts.reload();
  };

  return (
    <section aria-labelledby="transfers-heading">
      <h2 id="transfers-heading" className="esb-section-title">Transfers</h2>

      <ErrorNotice error={accounts.error} />
      <ErrorNotice error={beneficiaries.error} />

      <div aria-live="polite">
        {accounts.loading || beneficiaries.loading ? <p className="esb-loading">Loading…</p> : null}
      </div>

      <div className="esb-grid">
        <BeneficiaryTransferForm
          accounts={accountList}
          beneficiaries={beneficiaryList}
          onDone={reloadAll}
        />
        <InternalTransferForm accounts={accountList} onDone={reloadAll} />
      </div>
    </section>
  );
}
